import type { ComponentType, JSX, PropsWithChildren } from 'react';

import type { Translations } from '@change-corgi/core/i18n';
import { I18nOverrides } from '@change-corgi/core/react/i18n';
import { TrackingContext } from '@change-corgi/core/react/tracking';

import { useLocale } from 'src/app/shared/hooks/l10n';
import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import PAGE_I18N_RAW from './homepage-content.i18n.yml';
import type {
	HomepagePageCombinedState,
	HomepagePagePrefetchedContext,
	HomepagePagePrefetchedUserContext,
} from './hook';
import { usePageContext } from './hook';
import { HomepagePageDataContextProvider } from './pageData';
import { HomepagePageFcmContextProvider } from './pageFcm';
import { HomepagePageFcmExperimentsContextProvider } from './pageFcmExperiments';
import { HomepagePageUserDataContextProvider } from './pageUserData';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const PAGE_I18N = PAGE_I18N_RAW as Readonly<Record<string, Translations>>;

export type ErrorHandlerProps = { context: ErrorStateDetails<HomepagePageCombinedState> };

type Props = {
	prefetchedData: HomepagePagePrefetchedContext | undefined;
	prefetchedUserData: HomepagePagePrefetchedUserContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function HomepagePageContextProvider({
	children,
	prefetchedData,
	prefetchedUserData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(prefetchedUserData, prefetchedData);
	const locale = useLocale();

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData, pageUserData, pageFcm, pageFcmExperiments } = pageContextState;

	return (
		<I18nOverrides translations={PAGE_I18N[locale] ?? PAGE_I18N['en-US']}>
			<HomepagePageDataContextProvider pageData={pageData}>
				<HomepagePageUserDataContextProvider pageUserData={pageUserData}>
					<HomepagePageFcmContextProvider pageFcm={pageFcm}>
						<HomepagePageFcmExperimentsContextProvider pageFcmExperiments={pageFcmExperiments}>
							<TrackingContext properties={{ version: pageUserData.data.showV3 ? 'v3' : 'v2' }}>
								{children}
							</TrackingContext>
						</HomepagePageFcmExperimentsContextProvider>
					</HomepagePageFcmContextProvider>
				</HomepagePageUserDataContextProvider>
			</HomepagePageDataContextProvider>
		</I18nOverrides>
	);
}
